import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";

const links = [
  { to: "/story", label: "Story" },
  { to: "/shop", label: "Shop" },
  { to: "/gifts", label: "Gifts" },
  { to: "/commissions", label: "Commissions" },
  { to: "/care", label: "Care" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#E5E0D8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo-leather.png"
            alt="The Woodworkers Shed"
            className="h-10 md:h-11 w-auto select-none"
            draggable={false}
          />
          <span className="hidden sm:inline font-serif text-xl md:text-2xl tracking-tight">
            The Woodworkers Shed
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide font-medium ${
                  isActive ? "text-[#2C2421]" : "text-[#2C2421]/75 hover:text-[#2C2421]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/cart"
            data-testid="nav-cart-btn"
            aria-label="Cart"
            className="relative inline-flex items-center justify-center p-2 hover:text-[#6F4E37] transition-colors"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#2C2421] text-[#FDFBF7] text-[10px] font-medium rounded-full inline-flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E0D8] bg-[#FDFBF7]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-base font-medium"
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
